import { useEffect, useRef, useState } from 'react';
import { nb } from 'date-fns/locale';
import {
  Alert,
  Box,
  Button,
  Datepicker,
  Divider,
  Flex,
  FormField,
  HelpText,
  Input,
  InputGroup,
  Legend,
  Select,
  Text,
  Textarea,
  Title,
  Toggles,
  Tooltip,
} from '@gjensidige/builders-components';

import insuranceAdvisorImage from '../assets/insurance-advisor.webp';
import type { BefaringsekspertenLead, CreateLeadResponse, PriceEstimateResponse } from '../types';
import { api, ApiError } from '../utils/api';
import { formatDate, formatNok } from '../utils/format';
import { validators } from '../utils/validators';
import { CompletionIcon } from './CompletionIcon';
import { SuccessIcon } from './SuccessIcon';

// ─── Constants ────────────────────────────────────────────────────────────────

type BuildingTypeOption = {
  value: string;
  label: string;
};

const BUILDING_TYPES: BuildingTypeOption[] = [
  { value: 'BEFARING_I', label: '1–2 roms leilighet' },
  { value: 'BEFARING_II', label: '3 roms leilighet' },
  { value: 'BEFARING_III', label: '4+ roms leilighet' },
  { value: 'BEFARING_IIII', label: 'Enebolig / rekkehus' },
  { value: 'BEFARING_CABIN', label: 'Hytte' },
];

/** Virtual type used when any non-cabin building has BRA > 200 m². Not shown in the dropdown. */
const BEFARING_V_OPTION: BuildingTypeOption = {
  value: 'BEFARING_V',
  label: 'Enebolig / rekkehus / Leilighet over 200m²',
};

const EXTRAS_OPTIONS = [
  { value: 'hybel', label: 'Hybel/utleiedel' },
  { value: 'garasje', label: 'Garasje' },
  { value: 'annet', label: 'Annet' },
] as const;

const COUNT_OPTIONS = Array(9)
  .fill(0)
  .map((_, i) => String(i + 1));

/** Building types that cannot be priced automatically and require manual review. */
const MANUAL_TYPES = new Set(['BEFARING_CABIN']);

const STEPS = [
  { id: 'property', label: 'Info om boligen' },
  { id: 'schedule', label: 'Tid for befaring' },
  { id: 'contact', label: 'Kontaktinfo' },
  { id: 'review', label: 'Send inn' },
] as const;

type StepId = (typeof STEPS)[number]['id'];

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormErrors {
  buildingType?: string;
  street?: string;
  postalCode?: string;
  postPlace?: string;
  size?: string;
  bathrooms?: string;
  floors?: string;
  inspectionDate?: string;
  firstName?: string;
  lastName?: string;
  birthdate?: string;
  email?: string;
  phone?: string;
}

interface FormValues {
  buildingType: string;
  street: string;
  postalCode: string;
  postPlace: string;
  size: string;
  bathrooms: string;
  floors: string;
  extras: string[];
  extrasOther: string;
  inspectionDateKnown: string;
  inspectionDate: string;
  inspectionTime: string;
  firstName: string;
  lastName: string;
  birthdate: string;
  email: string;
  phone: string;
  referredBy: string;
  referredByPhone: string;
  consent: boolean;
  insuranceInterest: boolean;
}

const EMPTY: FormValues = {
  buildingType: '',
  street: '',
  postalCode: '',
  postPlace: '',
  size: '',
  bathrooms: '',
  floors: '',
  extras: [],
  extrasOther: '',
  inspectionDateKnown: '',
  inspectionDate: '',
  inspectionTime: '',
  firstName: '',
  lastName: '',
  birthdate: '',
  email: '',
  phone: '',
  referredBy: '',
  referredByPhone: '',
  consent: false,
  insuranceInterest: false,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const stepIndex = (id: StepId) => STEPS.findIndex((s) => s.id === id);

const toLocalIsoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

function NextSteps({ headingAs = 'h3' }: { headingAs?: 'h2' | 'h3' }) {
  return (
    <>
      <Title as={headingAs} size="5">
        Dette skjer videre
      </Title>
      <ol className="review-steps">
        <li>Henvendelsen sendes videre til en Befaringsekspert i nærheten.</li>
        <li>
          Befaringseksperten går over henvendelsen din og sender deg et tilbud innen 5 virkedager.
        </li>
        <li>Du får en epost med lenke til tilbudet som signeres digitalt.</li>
        <li>
          Etter signering kontakter Befaringseksperten deg innen 2 dager og dere avtaler befaringen
          nærmere.
        </li>
      </ol>
    </>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OrderForm() {
  const [step, setStep] = useState<StepId>('property');
  const [maxVisited, setMaxVisited] = useState(0); // highest step index reached
  const [values, setValues] = useState<FormValues>(EMPTY);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [interestConsent, setInterestConsent] = useState(false);
  const [showInterestThanks, setShowInterestThanks] = useState(false);
  const [priceEstimate, setPriceEstimate] = useState<PriceEstimateResponse | null>(null);
  const [isPriceFetching, setIsPriceFetching] = useState(false);
  // Only trigger a fetch when postalCode is exactly 4 digits or empty —
  // not on every intermediate keystroke.
  const effectivePostalCode = values.postalCode.length === 4 ? values.postalCode : undefined;

  // Fetch price estimate when building type or size changes.
  // Re-fetches only when postal code reaches exactly 4 digits (or is cleared).
  // Suppressed for manual types — they can't be auto-priced.
  // If BRA > 200 m² and the type is not a manual/cabin type, upgrade to BEFARING_V.
  //
  // Two safeguards:
  //   1. Debounce (350 ms) so typing "150" in `size` doesn't trigger three
  //      requests ("1", "15", "150"), reducing backend chatter.
  //   2. Monotonic request counter so out-of-order resolves (e.g. user
  //      changes building type twice quickly) don't overwrite the newest
  //      estimate with a stale one. The api client doesn't surface a
  //      signal/AbortController, hence the counter approach.
  const priceRequestId = useRef(0);
  useEffect(() => {
    const selectedType = BUILDING_TYPES.find((t) => t.value === values.buildingType);
    if (!selectedType || MANUAL_TYPES.has(values.buildingType)) {
      setPriceEstimate(null);
      return;
    }

    const sizeM2 = parseInt(values.size) || 0;
    const typeToFetch = sizeM2 >= 200 ? BEFARING_V_OPTION : selectedType;

    const timer = setTimeout(() => {
      const myRequestId = ++priceRequestId.current;
      setIsPriceFetching(true);
      api
        .post<PriceEstimateResponse>('/v1/open/pricing/estimate', {
          housingType: typeToFetch.value,
          ...(effectivePostalCode && { postalCode: effectivePostalCode }),
        })
        .then((res) => {
          if (myRequestId === priceRequestId.current) setPriceEstimate(res);
        })
        .catch(() => {
          if (myRequestId === priceRequestId.current) setPriceEstimate(null);
        })
        .finally(() => {
          if (myRequestId === priceRequestId.current) setIsPriceFetching(false);
        });
    }, 350);
    return () => clearTimeout(timer);
  }, [values.buildingType, values.size, effectivePostalCode]);

  // ── Field helpers ──────────────────────────────────────────────────────────

  const set = (field: keyof FormValues, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (field in errors) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // ── Navigation ────────────────────────────────────────────────────────────

  const goTo = (id: StepId) => {
    setErrors({});
    setStep(id);
    const idx = stepIndex(id);
    setMaxVisited((prev) => Math.max(prev, idx));
    window.scrollTo(0, 0);
  };

  const focusFirstError = () => {
    requestAnimationFrame(() => {
      const el = document.querySelector('[aria-invalid="true"]');
      if (el instanceof HTMLElement) el.focus();
    });
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const validateProperty = (): FormErrors => {
    const e: FormErrors = {};
    if (!values.buildingType) e.buildingType = 'Velg en boligtype';
    const streetErr = validators.required('Adresse')(values.street);
    if (streetErr) e.street = streetErr;
    const postalErr = validators.postalCode()(values.postalCode);
    if (postalErr) e.postalCode = postalErr;
    const postPlaceErr = validators.postPlace()(values.postPlace);
    if (postPlaceErr) e.postPlace = postPlaceErr;
    const sizeErr = validators.required('Areal')(values.size);
    if (sizeErr) e.size = sizeErr;
    if (!values.bathrooms) e.bathrooms = 'Velg antall bad';
    if (!values.floors) e.floors = 'Velg antall etasjer';
    return e;
  };

  const validateSchedule = (): FormErrors => {
    const e: FormErrors = {};
    // "Etter avtale" (inspectionDateKnown === 'no') means date is optional.
    // Otherwise a date must be picked.
    if (values.inspectionDateKnown !== 'no' && !values.inspectionDate)
      e.inspectionDate = 'Velg en dato for ferdigbefaringen';
    return e;
  };

  const validateContact = (): FormErrors => {
    // Note: `consent` is an optional marketing opt-in (GDPR). It is intentionally
    // not validated here — submitting `consent: false` is a valid, expected
    // outcome and the backend persists it as such.
    const e: FormErrors = {};
    const firstNameErr = validators.required('Fornavn')(values.firstName);
    if (firstNameErr) e.firstName = firstNameErr;
    const lastNameErr = validators.required('Etternavn')(values.lastName);
    if (lastNameErr) e.lastName = lastNameErr;
    const birthdateErr = validators.birthdate()(values.birthdate);
    if (birthdateErr) e.birthdate = birthdateErr;
    const emailErr = validators.email()(values.email);
    if (emailErr) e.email = emailErr;
    const phoneErr = validators.phone()(values.phone);
    if (phoneErr) e.phone = phoneErr;
    return e;
  };

  // ── Step transitions ──────────────────────────────────────────────────────

  const handleNextFromProperty = () => {
    const e = validateProperty();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      focusFirstError();
      return;
    }
    goTo('schedule');
  };

  const handleNextFromSchedule = () => {
    const e = validateSchedule();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      focusFirstError();
      return;
    }
    goTo('contact');
  };

  const handleNextFromContact = () => {
    const e = validateContact();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      focusFirstError();
      return;
    }
    goTo('review');
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    // Guard: re-validate all steps in case user navigated here via tab
    const propErrors = validateProperty();
    if (Object.keys(propErrors).length > 0) {
      setErrors(propErrors);
      goTo('property');
      focusFirstError();
      return;
    }
    const schedErrors = validateSchedule();
    if (Object.keys(schedErrors).length > 0) {
      setErrors(schedErrors);
      goTo('schedule');
      focusFirstError();
      return;
    }
    const contactErrors = validateContact();
    if (Object.keys(contactErrors).length > 0) {
      setErrors(contactErrors);
      goTo('contact');
      focusFirstError();
      return;
    }

    const lead: BefaringsekspertenLead = {
      firstName: values.firstName,
      lastName: values.lastName,
      birthdate: values.birthdate,
      email: values.email || null,
      phone: values.phone || null,
      street: values.street,
      postalCode: values.postalCode,
      postPlace: values.postPlace,
      consent: values.consent,
      buildingType: effectiveType ? [effectiveType.label] : [],
      datoForFerdigbefaring:
        values.inspectionDateKnown !== 'no' && values.inspectionDate ? values.inspectionDate : null,
      inspectionTime:
        values.inspectionDateKnown !== 'no' && values.inspectionTime ? values.inspectionTime : null,
      propertyDetails: {
        sizeM2: values.size || undefined,
        bathrooms: values.bathrooms || undefined,
        floors: values.floors || undefined,
        extras: values.extras.length > 0 ? values.extras : undefined,
        extrasDescription:
          values.extras.includes('annet') && values.extrasOther ? values.extrasOther : undefined,
      },
      insuranceInterest: values.insuranceInterest,
      referredByName: values.referredBy || null,
      referredByPhone: values.referredByPhone || null,
    };

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await api.post<CreateLeadResponse>('/v1/open/leads/form', lead);
      goTo('property');
      setValues(EMPTY);
      setPriceEstimate(null);
      setMaxVisited(0);
      setStep('property'); // intentionally reset after goTo resets errors
      // Show success by switching to a local flag — handled below
      setShowSuccess(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(
        err instanceof ApiError
          ? 'Noe gikk galt. Prøv igjen eller kontakt oss.'
          : 'En uventet feil oppstod. Prøv igjen.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const currentIdx = stepIndex(step);
  const selectedType = BUILDING_TYPES.find((t) => t.value === values.buildingType);
  const sizeM2 = parseInt(values.size) || 0;
  /** The type actually used for pricing and submission — BEFARING_V when BRA > 200 and not a cabin. */
  const effectiveType =
    selectedType && !MANUAL_TYPES.has(values.buildingType) && sizeM2 >= 200
      ? BEFARING_V_OPTION
      : selectedType;
  const isManual = MANUAL_TYPES.has(values.buildingType);

  // ── Success screen ────────────────────────────────────────────────────────

  if (showSuccess) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="md"
        className="status-screen submitted-screen"
      >
        <SuccessIcon />
        <Flex direction="column" align="center" gap="sm">
          <Title as="h1" size="4" style={{ textAlign: 'center' }}>
            Takk for henvendelsen din!
          </Title>
          <Text style={{ textAlign: 'center' }}>
            Vi har mottatt din henvendelse og vil ta kontakt med deg snart.
          </Text>
        </Flex>

        <Box padding="md" className="status-next-steps">
          <Flex layout={1} gap="sm">
            <NextSteps headingAs="h2" />
          </Flex>
        </Box>

        <Text style={{ textAlign: 'center' }}>Du kan lukke denne siden</Text>
      </Flex>
    );
  }

  // ── Interest registered screen ───────────────────────────────────────────

  if (showInterestThanks) {
    return (
      <Flex direction="column" align="center" justify="center" gap="xl" className="status-screen">
        <CompletionIcon />
        <Flex direction="column" align="center" gap="md">
          <Title as="h1" size="2" style={{ textAlign: 'center' }}>
            Takk for interessen!
          </Title>
          <Text style={{ textAlign: 'center' }}>
            Vi har registrert at du ønsker befaring i ditt område.
            <br />
            Vi tar kontakt dersom vi etablerer tjenesten der.
          </Text>
          <Text style={{ textAlign: 'center' }}>Du kan lukke denne siden</Text>
        </Flex>
      </Flex>
    );
  }

  // ── Card footer (price left, action buttons right) ───────────────────────

  const renderCardFooter = (
    primaryLabel: string,
    onPrimary: () => void,
    primaryLoading?: boolean,
  ) => {
    // Footer price summary lives only on step 1 (property). On review (step 4)
    // the price is shown inline in the summary section instead.
    // Hidden when exceedsMaxDistance — no meaningful price to show in that case.
    // Also hidden while fetching so stale exceedsMaxDistance doesn't suppress the footer.
    const showPrice = step === 'property' && !isPriceFetching && !priceEstimate?.exceedsMaxDistance;
    const isSubmit = primaryLabel === 'Send henvendelse';
    return (
      <Flex
        direction="row"
        justify={showPrice ? 'space-between' : 'end'}
        align="center"
        gap="md"
        wrap="nowrap"
        className="form-footer-row"
      >
        {/* Left: price summary — only on the property step */}
        {showPrice && (
          <Flex direction="column" gap="none" style={{ minWidth: 0 }}>
            <Flex gap="xs" align="center">
              <Text size="large" style={{ color: 'var(--builders-color-blue-600)' }}>
                Estimert totalsum:
              </Text>
              <Text size={'large'}>
                <strong>
                  {isPriceFetching
                    ? '...'
                    : priceEstimate
                      ? formatNok(priceEstimate.amountNok)
                      : '—'}
                </strong>
              </Text>
            </Flex>
            <Flex gap="xs" align="center">
              <Text size="small">
                Estimert kjøretillegg:{' '}
                {isPriceFetching
                  ? '...'
                  : priceEstimate?.drivingFeeNok != null
                    ? formatNok(priceEstimate.drivingFeeNok)
                    : '—'}
              </Text>
              <Tooltip
                size="sm"
                content="Kjøretillegg dekker reisekostnader for befaringseksperten til adressen din. Beløpet kan variere og avklares endelig i tilbudet."
              >
                <button
                  type="button"
                  aria-label="Mer info om kjøretillegg"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 'var(--builders-unit-20)',
                    height: 'var(--builders-unit-20)',
                    borderRadius: 'var(--builders-radius-full)',
                    border: '1.5px solid var(--builders-color-blue-400)',
                    background: 'transparent',
                    color: 'var(--builders-color-blue-600)',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    padding: 0,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  ?
                </button>
              </Tooltip>
            </Flex>
          </Flex>
        )}

        {/* Right: Neste steg button only (Tilbake dropped per design) */}
        <Flex gap="sm" align="center" style={{ flexShrink: 0 }}>
          <Button
            arrow={!isSubmit && !primaryLoading}
            onClick={onPrimary}
            loading={primaryLoading}
            disabled={primaryLoading}
          >
            {primaryLabel}
          </Button>
        </Flex>
      </Flex>
    );
  };

  // ── Step progress indicator (Toggles blue-grouped per Figma) ──────────────

  const stepProgress = (
    <Toggles
      variant="blue-grouped"
      fill
      value={STEPS[currentIdx].id}
      onChange={(val) => {
        const idx = STEPS.findIndex((s) => s.id === val);
        // Allow navigation to any visited step (forward steps gated by validation)
        if (idx >= 0 && idx <= maxVisited) goTo(STEPS[idx].id);
      }}
    >
      {STEPS.map((s, i) => (
        <Toggles.Toggle key={s.id} value={s.id} aria-label={s.label}>
          <span className="step-num">{i + 1}</span>
          <span className="step-label">{s.label}</span>
        </Toggles.Toggle>
      ))}
    </Toggles>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Flex layout={1} gap="lg" center="md">
      <Title as="h1" size="4" className="form-heading">
        Send henvendelse
      </Title>

      {stepProgress}

      {/* ── Step 1: Info om boligen ── */}
      {step === 'property' && (
        <>
          <Box variant="blue" padding="md" style={{ paddingTop: '45px' }}>
            <Flex direction="column" align="start" gap="md">
              <Title as="h2" size="4">
                Fortell om boligen din
              </Title>

              {/* Line 1: Boligtype dropdown */}
              <div className="field-md">
                <FormField
                  as={Select}
                  label="Velg boligtype"
                  helpText={{ children: 'Velg den boligtypen som passer best', toggle: '?' }}
                  value={values.buildingType}
                  onChange={(e) => set('buildingType', e.target.value)}
                  invalid={!!errors.buildingType}
                  validationMessage={errors.buildingType}
                >
                  <option value="">— Velg boligtype —</option>
                  {BUILDING_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </FormField>
              </div>

              {/* Line 2: Adresse */}
              <div className="field-md">
                <FormField
                  as={Input}
                  label="Adresse"
                  helpText={{ children: 'Gateadresse og husnummer', toggle: '?' }}
                  type="text"
                  value={values.street}
                  onChange={(e) => set('street', e.target.value)}
                  invalid={!!errors.street}
                  validationMessage={errors.street}
                />
              </div>

              {/* Line 3: Postkode + Poststed */}
              <div className="field-row-postal">
                <FormField
                  as={Input}
                  label="Postkode"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={values.postalCode}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    set('postalCode', digits);
                  }}
                  invalid={!!errors.postalCode}
                  validationMessage={errors.postalCode}
                />
                <FormField
                  as={Input}
                  label="Poststed"
                  type="text"
                  value={values.postPlace}
                  onChange={(e) => {
                    const letters = e.target.value.replace(/[^a-zA-ZæøåÆØÅ\s-]/g, '');
                    set('postPlace', letters);
                  }}
                  invalid={!!errors.postPlace}
                  validationMessage={errors.postPlace}
                />
              </div>

              {/* Line 4: Størrelse */}
              <div className="field-sm">
                <FormField
                  as={Input}
                  label="Hvor stor er boligen?"
                  type="text"
                  inputMode="numeric"
                  helpText={{ children: 'Oppgi bruksareal (BRA) i m²', toggle: '?' }}
                  value={values.size}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    set('size', digits);
                  }}
                  invalid={!!errors.size}
                  validationMessage={errors.size}
                />
              </div>

              {/* Line 5: Bad */}
              <div className="field-sm">
                <FormField
                  as={Select}
                  label="Hvor mange bad har boligen?"
                  helpText={{ children: 'Tell antall rom med dusj/badekar', toggle: '?' }}
                  value={values.bathrooms}
                  onChange={(e) => set('bathrooms', e.target.value)}
                  invalid={!!errors.bathrooms}
                  validationMessage={errors.bathrooms}
                >
                  <option value="">— Velg —</option>
                  {COUNT_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </FormField>
              </div>

              {/* Line 6: Etasjer */}
              <div className="field-sm">
                <FormField
                  as={Select}
                  label="Hvor mange etasjer har boligen?"
                  helpText={{ children: 'Inkluder kjeller og loft hvis innredet', toggle: '?' }}
                  value={values.floors}
                  onChange={(e) => set('floors', e.target.value)}
                  invalid={!!errors.floors}
                  validationMessage={errors.floors}
                >
                  <option value="">— Velg —</option>
                  {COUNT_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </FormField>
              </div>

              {/* Line 7: Extras */}
              <div className="field-md">
                <InputGroup
                  name="extras"
                  type="checkbox"
                  variant="column"
                  value={values.extras}
                  onChange={(vals) => setValues((prev) => ({ ...prev, extras: vals }))}
                >
                  <Legend>Er det andre ting du vil at vi sjekker?</Legend>
                  {EXTRAS_OPTIONS.map((o) => (
                    <InputGroup.Input
                      key={o.value}
                      label={o.value === 'annet' ? 'Annet (max 150 tegn)' : o.label}
                      value={o.value}
                    />
                  ))}
                </InputGroup>
              </div>

              {values.extras.includes('annet') && (
                <div className="field-md">
                  <FormField
                    as={Textarea}
                    label={{ children: 'Beskriv hva vi skal sjekke', hidden: true }}
                    placeholder="Skriv her..."
                    maxWarning={150}
                    rows={3}
                    value={values.extrasOther}
                    onChange={(e) => set('extrasOther', e.target.value)}
                  />
                </div>
              )}

              {/* Price estimate / manual notice */}
              {!isPriceFetching && isManual && values.buildingType && (
                <Alert severity="warning" variant="outlined">
                  Pris må beregnes av befaringseksperten. Fyll ut skjemaet, så sender vi et tilbud.
                </Alert>
              )}

              {!isPriceFetching && priceEstimate && priceEstimate.exceedsMaxDistance && (
                <Alert severity="warning" variant="outlined">
                  <Title as="h2" size="5">
                    Vi tilbyr dessverre ikke Befaringseksperten i ditt område
                  </Title>
                  <Text>
                    Kan vi registrere at du er interessert i tjenesten slik at vi vet at det er
                    etterspørsel etter befaringseksperter i området? Vi lagrer ingen
                    personalopplysninger om deg.
                  </Text>
                  <InputGroup
                    name="interestConsent"
                    type="checkbox"
                    variant="column"
                    value={interestConsent ? ['yes'] : []}
                    onChange={(vals) => setInterestConsent(vals.includes('yes'))}
                    style={{ marginTop: 'var(--builders-unit-10)' }}
                  >
                    <InputGroup.Input label="Ja, registrer interessen i området" value="yes" />
                  </InputGroup>
                </Alert>
              )}

              {priceEstimate?.exceedsMaxDistance
                ? renderCardFooter('Avslutt', () => setShowInterestThanks(true))
                : renderCardFooter('Neste steg', handleNextFromProperty)}
            </Flex>
          </Box>
        </>
      )}

      {/* ── Step 2: Tid for befaring ── */}
      {step === 'schedule' && (
        <>
          <Box variant="blue" padding="lg" style={{ paddingTop: '45px' }}>
            <Flex layout={1} gap="lg">
              <Title as="h2" size="4">
                Når er ferdigbefaring?
              </Title>

              <Flex direction="column" align="start" gap="md">
                <div className="field-md">
                  <FormField
                    as={Datepicker}
                    label="Dato for ferdigbefaring"
                    helpText={{
                      children:
                        'Velg ønsket dato for befaringen. Vi anbefaler minst 14 dager frem i tid.',
                      toggle: '?',
                    }}
                    locale={nb}
                    labelDropdownMonth="Måned"
                    labelDropdownYear="År"
                    labelToggleButton="Åpne kalender"
                    min={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                    value={values.inspectionDate || undefined}
                    disabled={values.inspectionDateKnown === 'no'}
                    invalid={!!errors.inspectionDate}
                    validationMessage={errors.inspectionDate}
                    onChange={(date: Date | undefined) =>
                      set('inspectionDate', date ? toLocalIsoDate(date) : '')
                    }
                  />
                </div>

                <div className="field-md">
                  <FormField
                    as={Input}
                    // Builders Input typings omit "time"; runtime accepts it fine.
                    // TODO: remove cast when @gjensidige/builders-components Input typings include type="time".
                    {...({ type: 'time' } as unknown as { type: 'text' })}
                    label="Klokkeslett"
                    helpText={{
                      children: 'Oppgi ønsket tidspunkt på dagen for befaringen.',
                      toggle: '?',
                    }}
                    value={values.inspectionTime}
                    disabled={values.inspectionDateKnown === 'no'}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      set('inspectionTime', e.target.value)
                    }
                  />
                </div>
              </Flex>

              <InputGroup
                name="byArrangement"
                type="checkbox"
                value={values.inspectionDateKnown === 'no' ? ['yes'] : []}
                onChange={(vals) => {
                  const checked = vals.includes('yes');
                  set('inspectionDateKnown', checked ? 'no' : 'yes');
                  if (checked) {
                    set('inspectionDate', '');
                    set('inspectionTime', '');
                  }
                }}
              >
                <Legend>
                  Er du usikker på når du skal ha ferdigbefaring?{' '}
                  <HelpText toggle="?">
                    Vi tar kontakt med deg for å avtale et tidspunkt som passer.
                  </HelpText>
                </Legend>
                <InputGroup.Input label="Ja" value="yes" />
              </InputGroup>

              {renderCardFooter('Neste steg', handleNextFromSchedule)}
            </Flex>
          </Box>
        </>
      )}

      {/* ── Step 3: Kontaktinfo ── */}
      {step === 'contact' && (
        <>
          <Box variant="blue" padding="md" style={{ paddingTop: '45px' }}>
            <Flex layout={1} gap="md">
              <Title as="h2" size="4">
                Kontaktinformasjon
              </Title>

              <Flex direction="column" align="start" gap="md">
                <div className="field-md">
                  <FormField
                    as={Input}
                    label="Fornavn"
                    type="text"
                    helpText={{ children: 'Oppgi fornavnet ditt.', toggle: '?' }}
                    value={values.firstName}
                    onChange={(e) => set('firstName', e.target.value)}
                    invalid={!!errors.firstName}
                    validationMessage={errors.firstName}
                  />
                </div>

                <div className="field-md">
                  <FormField
                    as={Input}
                    label="Etternavn"
                    type="text"
                    helpText={{ children: 'Oppgi etternavnet ditt.', toggle: '?' }}
                    value={values.lastName}
                    onChange={(e) => set('lastName', e.target.value)}
                    invalid={!!errors.lastName}
                    validationMessage={errors.lastName}
                  />
                </div>

                <div className="field-md">
                  <FormField
                    as={Datepicker}
                    label="Fødselsdato"
                    helpText={{
                      children: 'Vi bruker fødselsdato for identifisering.',
                      toggle: '?',
                    }}
                    locale={nb}
                    labelDropdownMonth="Måned"
                    labelDropdownYear="År"
                    labelToggleButton="Åpne kalender"
                    max={new Date()}
                    min={new Date('1900-01-01')}
                    value={values.birthdate || undefined}
                    invalid={!!errors.birthdate}
                    validationMessage={errors.birthdate}
                    onChange={(date: Date | undefined) =>
                      set('birthdate', date ? toLocalIsoDate(date) : '')
                    }
                  />
                </div>

                <div className="field-md">
                  <FormField
                    as={Input}
                    label="E-post"
                    type="email"
                    helpText={{
                      children: 'Vi sender bekreftelse og tilbud til denne adressen.',
                      toggle: '?',
                    }}
                    value={values.email}
                    onChange={(e) => set('email', e.target.value)}
                    invalid={!!errors.email}
                    validationMessage={errors.email}
                  />
                </div>

                <div className="field-md">
                  <FormField
                    as={Input}
                    label="Telefonnummer"
                    type="tel"
                    helpText={{
                      children: 'Vi bruker telefonnummer for å avtale tidspunkt.',
                      toggle: '?',
                    }}
                    value={values.phone}
                    onChange={(e) => set('phone', e.target.value)}
                    invalid={!!errors.phone}
                    validationMessage={errors.phone}
                  />
                </div>

                {/* Referral fields may become relevant again in the future. */}
                {/*
                  <div className="field-md">
                    <div className="referral-box">
                      <Flex layout={1} gap="sm">
                        <Label>
                          Har du blitt vervet? Da kan du skrive navnet og telefonnummeret til den som
                          vervet deg. Så kan dere begge få rabatt.
                        </Label>
                        <FormField
                          as={Input}
                          label="Fullt navn"
                          type="text"
                          value={values.referredBy}
                          onChange={(e) => set('referredBy', e.target.value)}
                        />
                        <FormField
                          as={Input}
                          label="Telefonnummer"
                          type="tel"
                          value={values.referredByPhone}
                          onChange={(e) => set('referredByPhone', e.target.value)}
                        />
                      </Flex>
                    </div>
                  </div>
                */}
              </Flex>

              {renderCardFooter('Neste steg', handleNextFromContact)}
            </Flex>
          </Box>
        </>
      )}

      {/* ── Step 4: Oppsummering / Send inn ── */}
      {step === 'review' && (
        <>
          <Box variant="blue" padding="md" style={{ paddingTop: '45px' }}>
            <Flex layout={1} gap="md">
              <Title as="h2" size="4">
                Se over før du sender
              </Title>
              <Text as="p" weight={500}>
                Da er vi klare til å sende henvendelsen. Her er en oppsummering av det vi sender
                inn.
              </Text>

              {/* Prisforespørsel fra */}
              <Flex layout={1} gap="xs" className="review-section">
                <Text style={{ fontWeight: 700 }}>Prisforespørsel fra</Text>
                <Text>
                  Navn: {values.firstName} {values.lastName}
                </Text>
                {values.birthdate && <Text>Født: {formatDate(values.birthdate)}</Text>}
                {values.email && <Text>E-post: {values.email}</Text>}
                {values.phone && <Text>Telefonnummer: {values.phone}</Text>}
                {values.referredBy && <Text>Vervet av: {values.referredBy}</Text>}
                {values.referredByPhone && <Text>Telefon vervet av: {values.referredByPhone}</Text>}
              </Flex>

              {/* Ønsker befaring av */}
              <Flex layout={1} gap="xs" className="review-section">
                <Text style={{ fontWeight: 700 }}>Ønsker befaring av</Text>
                <Text>Boligtype: {effectiveType?.label ?? values.buildingType}</Text>
                {values.street && <Text>Adresse: {values.street}</Text>}
                {values.postalCode && <Text>Postnr: {values.postalCode}</Text>}
                {values.postPlace && <Text>Sted: {values.postPlace}</Text>}
              </Flex>

              {/* Detaljer på boligen */}
              <Flex layout={1} gap="xs" className="review-section">
                <Text style={{ fontWeight: 700 }}>Detaljer på boligen</Text>
                {values.size && <Text>Størrelse: {values.size}m²</Text>}
                {values.bathrooms && <Text>Ant bad: {values.bathrooms}</Text>}
                {values.floors && <Text>Ant etasjer: {values.floors}</Text>}
                {values.extras.length > 0 && (
                  <Text>
                    Andre ting:{' '}
                    {values.extras
                      .map((v) => {
                        if (v === 'annet' && values.extrasOther)
                          return `Annet (${values.extrasOther})`;
                        return EXTRAS_OPTIONS.find((o) => o.value === v)?.label ?? v;
                      })
                      .join(', ')}
                  </Text>
                )}
              </Flex>

              {/* Dato for befaring */}
              <Flex layout={1} gap="xs" className="review-section">
                <Text style={{ fontWeight: 700 }}>Dato for befaring</Text>
                {values.inspectionDateKnown === 'no' || !values.inspectionDate ? (
                  <Text>Kunde er usikker på dato. Kontakt for nærmere avtale.</Text>
                ) : (
                  <Text>
                    {formatDate(values.inspectionDate)}
                    {values.inspectionTime ? ` kl. ${values.inspectionTime}` : ''}
                  </Text>
                )}
              </Flex>

              {/* Estimert totalsum */}
              {priceEstimate && !priceEstimate.exceedsMaxDistance && (
                <Flex layout={1} gap="xs" className="review-section">
                  <Text style={{ fontWeight: 700 }}>Estimert totalsum</Text>
                  <Text size="large">{formatNok(priceEstimate.amountNok)}</Text>
                  {priceEstimate.drivingFeeNok != null && (
                    <Text>Estimert kjøretillegg: {formatNok(priceEstimate.drivingFeeNok)}</Text>
                  )}
                </Flex>
              )}
              {isManual && (
                <Alert severity="warning" variant="outlined">
                  Pris må beregnes av befaringseksperten. Vi sender deg et tilbud.
                </Alert>
              )}

              {/* Cross-sell: insurance advisory (Blue/200 nested box) */}
              <div className="cross-sell-box">
                <Flex layout={1} gap="sm">
                  <img className="advisor-photo" src={insuranceAdvisorImage} alt="" />
                  <Title as="h3" size="4">
                    Vil du snakke med en rådgiver om forsikring?
                  </Title>
                  <Text>
                    En rådgiver kan gå gjennom forsikringsbehovene dine og se om du kan få riktige
                    dekninger.
                  </Text>
                  <Text weight={700}>Rådgiveren hjelper deg blant annet med å:</Text>
                  <ul className="advisor-benefits">
                    <li>
                      Finne hvilke forsikringer som passer for deg, familiesituasjonen og økonomien
                      din
                    </li>
                    <li>Sjekke om du er over- eller underforsikret</li>
                    <li>Få oversikt over fordelene dine hos oss</li>
                  </ul>
                  <Text>
                    Rådgiveren setter opp et uforpliktende tilbud med eventuelle rabatter du har
                    krav på.
                  </Text>
                  <Divider color="200" />
                  <InputGroup
                    name="insuranceInterest"
                    type="checkbox"
                    variant="column"
                    value={values.insuranceInterest ? ['yes'] : []}
                    onChange={(vals) => set('insuranceInterest', vals.includes('yes'))}
                  >
                    <InputGroup.Input
                      label="Ja, jeg ønsker å bli kontaktet av Gjensidige på telefon"
                      helpText={{
                        children: 'Vi ringer deg for en uforpliktende prat om forsikring',
                        className: 'insurance-contact-help',
                      }}
                      value="yes"
                    />
                  </InputGroup>
                </Flex>
              </div>

              <NextSteps />

              {submitError && (
                <Alert severity="error" variant="filled">
                  {submitError}
                </Alert>
              )}

              {renderCardFooter('Send henvendelse', handleSubmit, isSubmitting)}
            </Flex>
          </Box>
        </>
      )}
    </Flex>
  );
}
